import { Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { isEmbed } from '@databases/modes';
import usePanel from '@databases/panel/usePanel';
import React, { type FunctionComponent, useMemo } from 'react';
import { Panel } from 'react-resizable-panels';
import { PanelResizeHandle } from 'react-resizable-panels';
import styled, { css } from 'styled-components';

import PanelSizes from './panel/sizes';
import { storage as panelPersistedStorage } from './panel/storage';
import Z_INDEX from './z-index';

const SidebarPanel = styled(Panel)<{ $showTopBorder: boolean }>`
  ${(prop) =>
    prop.$showTopBorder &&
    css`
      border-top: 1px solid ${token('color.border', '#EBECF0')};
      margin-top: ${token('space.100')};
    `}
  z-index: ${Z_INDEX.SIDE_PANEL_CONTAINER};
  min-width: 300px; /* Ensure minimum width in pixels regardless of viewport size */
`;

const componentStyles = xcss({
  width: '100%',
  height: '100%',
  overflow: 'auto',
});

interface SidePanelProps {
  sidebar?: React.ReactNode | null;
  showUpdatedSidePanel: boolean;
  options: {
    toolbarLocation: 'top' | 'views';
  };
}

/**
 * Exported only for testing.
 */
export function getDefaultPanelSize(
  sidebarType: 'sidebar' | 'newSidebar' | 'devtools',
  storageKey: string
) {
  const layout = panelPersistedStorage.getItem(storageKey);

  return layout
    ? layout[PanelSizes[sidebarType].order - 1]
    : PanelSizes[sidebarType as keyof typeof PanelSizes].defaultSize;
}

export function createPanelStorageKey(panelKey: string | undefined) {
  const isEmbedView = isEmbed();

  return `${isEmbedView ? 'embed' : 'confluence-content'}-${panelKey ?? 'no-panel'}`;
}

const SidePanel: FunctionComponent<SidePanelProps> = ({
  sidebar,
  options,
  showUpdatedSidePanel,
}) => {
  const { getPanelComponent, getPanelKey } = usePanel();
  const panelKey = getPanelKey();
  const storageKey = useMemo(() => createPanelStorageKey(panelKey), [panelKey]);

  /**
   * If a sidebar component is passed, that takes priority.
   * If a panel key is set, this defers to the panel component.
   */
  const sidebarComponent = useMemo(() => {
    if (sidebar) {
      return sidebar;
    }

    if (panelKey) {
      return getPanelComponent();
    }

    return null;
  }, [getPanelComponent, panelKey, sidebar]);

  const sidebarPanelProps = useMemo(() => {
    const isDevtools = panelKey === 'DevtoolsPanel';
    const panelType = isDevtools
      ? 'devtools'
      : showUpdatedSidePanel
      ? 'newSidebar'
      : 'sidebar';
    const panelConfig = PanelSizes[panelType];

    return {
      $showTopBorder: !showUpdatedSidePanel && options?.toolbarLocation === 'top',
      order: panelConfig.order,
      maxSize: panelConfig.max,
      minSize: panelConfig.min,
      defaultSize: getDefaultPanelSize(panelType, storageKey),
      'data-testid': showUpdatedSidePanel
        ? 'layout-right-side-panel'
        : 'legacy-layout-right-side-panel',
    };
  }, [showUpdatedSidePanel, storageKey, options?.toolbarLocation, panelKey]);

  const resizeHandleStyle = useMemo(
    () => ({
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
      marginTop:
        !showUpdatedSidePanel && options?.toolbarLocation === 'top'
          ? token('space.100')
          : 0,
      marginLeft: 0,
      marginRight: 0,
      marginBottom: 0,
    }),
    [showUpdatedSidePanel, options?.toolbarLocation]
  );

  if (!sidebarComponent) {
    return null;
  }

  return (
    <>
      <PanelResizeHandle id="app-resize-handle" style={resizeHandleStyle} />

      <SidebarPanel {...sidebarPanelProps} id="right-side-panel">
        <Flex
          justifyContent="start"
          alignItems="center"
          direction="column"
          xcss={componentStyles}
        >
          {sidebarComponent}
        </Flex>
      </SidebarPanel>
    </>
  );
};

export default SidePanel;
